import React, { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { getProductsController } from "../../../node_modules/shops-query/src/modules/products/index.js"
import { getProductsByCategoryController } from "../../../node_modules/shops-query/src/modules/productByCategory/index.js"
import { getProductsBySpecification } from "../../../node_modules/shops-query/src/modules/productBySpecification/index.js"
import { getFilterMasterByShop } from "../../../node_modules/shops-query/src/modules/filtermaster/index.js";
import { shopid } from "../../utils/url";
import ProductCard from "../productCard";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Loading from "../loading/index.js";

const AccordionSection = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-neutral-black hover:bg-gray-50 transition-colors duration-200"
        style={{ fontWeight: 600, fontSize: "18px" }}
      >
        <span>{title}</span>
        <span className="text-gray-500 transition-transform duration-200">
          {open ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm text-gray-600 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

const AllProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const categoryParam = searchParams.get("category");
  const mainCategoryParam = searchParams.get("mainCategory");
  const sortParam = searchParams.get("sort");
  const searchQueryParam = searchParams.get("search");
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState("popular");
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState(searchQueryParam || "");
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  // State for filter master data
  const [filterMasterData, setFilterMasterData] = useState(null);
  const [filterMasterLoading, setFilterMasterLoading] = useState(false);
  const [filterMasterError, setFilterMasterError] = useState(null);

  // Fetch filter master data when category changes
  useEffect(() => {
    const fetchFilterMaster = async () => {
      try {
        setFilterMasterLoading(true);
        setFilterMasterError(null);
        const payload = { shopId: shopid };
        if (categoryParam) payload.categoryName = categoryParam;
        if (mainCategoryParam) payload.masterCategoryName = mainCategoryParam;

        const results = await getFilterMasterByShop(payload);
        setFilterMasterData({ getFilterMasterByShop: results });
      } catch (error) {
        console.error('Error fetching filter master:', error);
        setFilterMasterError(error);
      } finally {
        setFilterMasterLoading(false);
      }
    };

    fetchFilterMaster();
  }, [categoryParam, mainCategoryParam]);



  // State for products by specification with search
  const [productBySpecData, setProductBySpecData] = useState(null);
  const [productBySpecLoading, setProductBySpecLoading] = useState(false);
  const [productBySpecError, setProductBySpecError] = useState(null);

  // Function to fetch products by specification
  const fetchProductBySpec = async () => {
    const shouldSkip = Object.keys(selectedFilters).length === 0 && !searchQuery;

    if (shouldSkip) {
      setProductBySpecData(null);
      setProductBySpecLoading(false);
      return;
    }

    try {
      setProductBySpecLoading(true);
      setProductBySpecError(null);

      const specifications = Object.keys(selectedFilters).length > 0
        ? Object.keys(selectedFilters).map((key) => ({
          mastername: key,
          value: selectedFilters[key],
        }))
        : [];

      const results = await getProductsBySpecification({
        shopId: shopid,
        specifications,
        searchKey: searchQuery || null,
      });

      setProductBySpecData({ productBySpecification: results });
    } catch (error) {
      console.error('Error fetching products by specification:', error);
      setProductBySpecError(error);
    } finally {
      setProductBySpecLoading(false);
    }
  };

  useEffect(() => {
    if (sortParam) {
      setSortBy(sortParam);
    }
    if (searchQueryParam) {
      setSearchQuery(searchQueryParam);
    }
  }, [sortParam, searchQueryParam]);

  useEffect(() => {
    // Scroll to top smoothly when route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  // Handle search query changes - ALWAYS refetch on any change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        // Search has content - force refetch
        setLoading(true);
        fetchProductBySpec()
          .then(() => {
            setLoading(false);
          })
          .catch((err) => {
            console.error("Search refetch error:", err);
            setLoading(false);
          });
      } else if (!searchQuery && Object.keys(selectedFilters).length === 0 && originalProducts.length > 0) {
        // No search and no filters - show original products
        setProducts(originalProducts);
        setLoading(false);
      }
    }, 400); // Debounce time

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, selectedFilters, originalProducts]);

  // Handle product data from specification query (for both filters and search)
  useEffect(() => {
    if (productBySpecData) {
      // Check if data exists - ignore errors if data is present
      const hasData = productBySpecData.productBySpecification &&
        Array.isArray(productBySpecData.productBySpecification);

      if (hasData && productBySpecData.productBySpecification.length > 0) {
        // Data exists and is not empty - show it even if there are errors
        setProducts(productBySpecData.productBySpecification);
        setLoading(false);
      } else if (hasData && productBySpecData.productBySpecification.length === 0) {
        // Data exists but is empty - this is valid
        setProducts([]);
        setLoading(false);
      } else if (Object.keys(selectedFilters).length > 0 || searchQuery) {
        // If filters or search are applied but no products found
        setProducts([]);
        setLoading(false);
      }
    }
  }, [productBySpecData, selectedFilters, searchQuery]);

  // Show loading when filters or search are active and query is running
  useEffect(() => {
    if (
      (Object.keys(selectedFilters).length > 0 || searchQuery) &&
      productBySpecLoading
    ) {
      setLoading(true);
    }
  }, [productBySpecLoading, selectedFilters, searchQuery]);

  useEffect(() => {
    if (productBySpecError) {
      // Only log non-critical errors (errors when there's no data)
      if (!productBySpecData || !productBySpecData.productBySpecification) {
        console.error("Product By Specification Error:", productBySpecError);
      } else {
        // Error exists but data is present - just log as warning
        console.warn("Partial data response with error:", productBySpecError);
      }
    }
  }, [productBySpecError, productBySpecData]);

  // Function to remove duplicates from array
  const getUniquValues = (array) => {
    return [...new Set(array)];
  };

  // Function to capitalize first letter
  const capitalizeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("search", searchQuery.trim());
      setSearchParams(newSearchParams);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("search");
    setSearchParams(newSearchParams);

    // Reset to original products
    if (originalProducts.length > 0) {
      setProducts(originalProducts);
    }
  };

  // Handle checkbox change
  const handleFilterChange = (filterName, value, isChecked) => {
    setSelectedFilters((prev) => {
      const updated = { ...prev };
      if (!updated[filterName]) {
        updated[filterName] = [];
      }
      if (isChecked) {
        if (!updated[filterName].includes(value)) {
          updated[filterName].push(value);
        }
      } else {
        updated[filterName] = updated[filterName].filter((v) => v !== value);
        if (updated[filterName].length === 0) {
          delete updated[filterName];
        }
      }
      return updated;
    });
  };

  // Clear all filters and reset to original products
  const clearAllFilters = () => {
    setSelectedFilters({});
    // Reset to original products when clearing filters
    if (originalProducts.length > 0) {
      setProducts(originalProducts);
    }
  };

  // Fetch products when filters change
  useEffect(() => {
    if (Object.keys(selectedFilters).length > 0) {
      fetchProductBySpec();
    }
  }, [selectedFilters]);

  // Reset products to original when all filters are cleared and no search
  useEffect(() => {
    if (
      Object.keys(selectedFilters).length === 0 &&
      !searchQuery &&
      originalProducts.length > 0
    ) {
      setProducts(originalProducts);
    }
  }, [selectedFilters, searchQuery, originalProducts]);

  const filteredProducts = React.useMemo(() => {
    let result = [...products];

    switch (sortBy) {
      case "popular":
        result = [...products];
        break;
      case "new_arrivals":
        result.sort((a, b) => {
          const dateA = new Date(a.addedon);
          const dateB = new Date(b.addedon);
          return dateB - dateA;
        });
        break;
      case "discount":
        result = [...products].sort(
          (a, b) => (b.discount || 0) - (a.discount || 0)
        );
        break;
      case "price_high":
        result = [...products].sort((a, b) => (b.prize || 0) - (a.prize || 0));
        break;
      case "price_low":
        result = [...products].sort((a, b) => (a.prize || 0) - (b.prize || 0));
        break;
      default:
        result = [...products];
    }

    return result;
  }, [sortBy, products]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load initial products and store them as original
  useEffect(() => {
    const loadProductsByMainOrSubCategory = async () => {
      // If search query exists, let the GraphQL query handle it
      // Don't modify loading state here if we are searching
      if (searchQuery) {
        return;
      }

      try {
        setLoading(true);

        // Case 1: only main category selected
        if (mainCategoryParam && !categoryParam) {
          const response = await getProductsByCategoryController(
            mainCategoryParam,
            shopid,
            null
          );
          setProducts(response || []);
          setOriginalProducts(response || []);
          return;
        }

        // Case 2: sub-category selected
        if (categoryParam) {
          const response = await getProductsByCategoryController(
            mainCategoryParam || "Men",
            shopid,
            categoryParam
          );
          setProducts(response || []);
          setOriginalProducts(response || []);
          return;
        }

        // Case 3: no categories → load all products
        if (!mainCategoryParam && !categoryParam) {
          const response = await getProductsController(shopid);
          setProducts(response || []);
          setOriginalProducts(response || []);
        }
      } catch (error) {
        console.log("Error loading products:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProductsByMainOrSubCategory();
  }, [mainCategoryParam, categoryParam, searchQuery]);

  // Generate breadcrumb items based on the current path
  const getBreadcrumbItems = () => {
    const items = [
      <Link
        key="home"
        underline="hover"
        href="/"
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          fontWeight: 400,
          color: "#C4C4C4",
        }}
      >
        Home
      </Link>,
    ];

    // If search query exists
    if (searchQuery) {
      items.push(
        <Typography
          key="search-results"
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "16px",
            fontWeight: 500,
            color: "#101011",
          }}
        >
          Search: "{searchQuery}"
        </Typography>
      );
    } else {
      // Add main category if exists
      if (mainCategoryParam) {
        items.push(
          <Link
            key="main-category"
            underline="hover"
            href={`/?mainCategory=${mainCategoryParam}`}
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 400,
              color: "#C4C4C4",
            }}
          >
            {mainCategoryParam}
          </Link>
        );
      }

      // Add sub-category if exists
      if (categoryParam) {
        items.push(
          <Typography
            key="sub-category"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#101011",
            }}
          >
            {categoryParam}
          </Typography>
        );
      } else if (mainCategoryParam) {
        // If only main category exists, make it the last item
        items[items.length - 1] = (
          <Typography
            key="main-category-last"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#101011",
            }}
          >
            {mainCategoryParam}
          </Typography>
        );
      } else {
        // If no categories, show "All Products" as last item
        items.push(
          <Typography
            key="all-products"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#101011",
            }}
          >
            All Products
          </Typography>
        );
      }
    }

    return items;
  };

  return (
    <div className="bg-white min-h-screen px-4 md:px-8 lg:px-16 py-6 font-poppins md:mb-32">
      <div className="max-w-8xl mx-auto">
        {/* Breadcrumb & Title */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex-1">
            <Breadcrumbs
              separator="›"
              aria-label="breadcrumb"
              className="text-[16px] mb-1"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "16px",
                fontWeight: 400,
                "& .MuiBreadcrumbs-separator:last-of-type": {
                  display: "none",
                },
              }}
            >
              {getBreadcrumbItems()}
            </Breadcrumbs>
          </div>

          {/* Search Bar - Visible on all routes */}
          {/* <div className="flex-1 max-w-md">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-transparent"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiSearch size={20} />
              </button>
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </form>
          </div> */}

          <div className="md:hidden flex items-center justify-between w-full mb-4">
            <button
              onClick={() => setShowMobileFilters(true)}
              className="px-0 py-2 bg-transparent font-semibold text-neutral-black"
              style={{ fontSize: "18px" }}
            >
              Filters
            </button>
            <div className="flex items-center gap-2">
              <div className="relative">
                <div
                  onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                  className="px-4 py-2 bg-white border border-gray-200 rounded text-[14px] text-gray-900 cursor-pointer min-w-[160px] flex items-center justify-between"
                  style={{ border: '1px solid #E5E7EB' }}
                >
                  <span className="font-medium text-[#4B5563]">
                    Sort by: <span className="font-bold text-black">{sortBy === "popular" ? "Popular" : sortBy === "new_arrivals" ? "New Arrivals" : sortBy === "discount" ? "Discount" : sortBy === "price_high" ? "Price - High to Low" : "Price - Low to High"}</span>
                  </span>
                  <FiChevronDown className={`ml-3 text-black transition-transform ${isSortDropdownOpen ? 'rotate-180' : ''}`} size={18} />
                </div>
                {isSortDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[90]"
                      onClick={() => setIsSortDropdownOpen(false)}
                    />
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-100 rounded shadow-lg z-[100] py-2">
                      {[
                        { label: "Popular", value: "popular" },
                        { label: "New Arrivals", value: "new_arrivals" },
                        { label: "Discount", value: "discount" },
                        { label: "Price - High to Low", value: "price_high" },
                        { label: "Price - Low to High", value: "price_low" },
                      ].map((option) => (
                        <div
                          key={option.value}
                          className={`px-4 py-3 text-[14px] cursor-pointer hover:bg-gray-50 transition-colors ${sortBy === option.value ? 'font-semibold text-black bg-gray-50' : 'text-[#4B5563]'}`}
                          onClick={() => {
                            setSortBy(option.value);
                            setIsSortDropdownOpen(false);
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar - Hide when searching */}
          {!searchQuery && (
            <div className="hidden md:block w-64 flex-shrink-0">
              <div className="sticky top-6 max-h-[calc(100vh-120px)] overflow-y-auto">
                <div className="px-4 py-3 flex items-center justify-between bg-white border-b border-gray-200">
                  <h4
                    className="text-neutral-black font-semibold"
                    style={{ fontWeight: 600, fontSize: "18px" }}
                  >
                    Filters
                  </h4>

                  {Object.keys(selectedFilters).length > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-[18px] uppercase font-semibold text-primary-default underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                <div
                  className="divide-y rounded-lg bg-white"
                  style={{ border: "1px solid #0000001A" }}
                >
                  {filterMasterData?.getFilterMasterByShop &&
                    filterMasterData.getFilterMasterByShop.length > 0 ? (
                    filterMasterData.getFilterMasterByShop.map(
                      (filter, index) => (
                        <AccordionSection
                          key={index}
                          title={capitalizeFirstLetter(
                            filter.specificationMaster
                          )}
                        >
                          {getUniquValues(filter.specificationValue).map(
                            (value, idx) => (
                              <label
                                key={idx}
                                className="flex items-center gap-2 mt-2 hover:bg-gray-50 p-2 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="cursor-pointer"
                                  checked={
                                    selectedFilters[
                                      filter.specificationMaster
                                    ]?.includes(value) || false
                                  }
                                  onChange={(e) =>
                                    handleFilterChange(
                                      filter.specificationMaster,
                                      value,
                                      e.target.checked
                                    )
                                  }
                                />
                                <span className="text-gray-700">{value}</span>
                              </label>
                            )
                          )}
                        </AccordionSection>
                      )
                    )
                  ) : (
                    null
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Products Section - Scrollable on desktop */}
          <div className={`${searchQuery ? "w-full" : "flex-1"} min-h-screen`}>
            {/* Heading and Sort by inline */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[20px] font-bold text-gray-900">
                {searchQuery
                  ? `Search Results for "${searchQuery}"`
                  : categoryParam
                    ? categoryParam
                    : mainCategoryParam
                      ? mainCategoryParam
                      : "All Products"}
                <span className="text-[20px] text-neutral-black font-light">
                  ({filteredProducts?.length || 0} products)
                </span>
              </h2>

              <div className="hidden md:flex items-center gap-2">
                <div className="relative">
                  <div
                    onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                    className="px-4 py-2 bg-white border border-gray-200 rounded text-[14px] text-gray-900 cursor-pointer min-w-[200px] flex items-center justify-between"
                    style={{ border: '1px solid #E5E7EB' }}
                  >
                    <span className="font-medium text-[#4B5563]">
                      Sort by: <span className="font-bold text-black">{sortBy === "popular" ? "Popular" : sortBy === "new_arrivals" ? "New Arrivals" : sortBy === "discount" ? "Discount" : sortBy === "price_high" ? "Price - High to Low" : "Price - Low to High"}</span>
                    </span>
                    <FiChevronDown className={`ml-3 text-black transition-transform ${isSortDropdownOpen ? 'rotate-180' : ''}`} size={18} />
                  </div>
                  {isSortDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-[90]"
                        onClick={() => setIsSortDropdownOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-100 rounded shadow-lg z-[100] py-2">
                        {[
                          { label: "Popular", value: "popular" },
                          { label: "New Arrivals", value: "new_arrivals" },
                          { label: "Discount", value: "discount" },
                          { label: "Price - High to Low", value: "price_high" },
                          { label: "Price - Low to High", value: "price_low" },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`px-4 py-3 text-[14px] cursor-pointer hover:bg-gray-50 transition-colors ${sortBy === option.value ? 'font-semibold text-black bg-gray-50' : 'text-[#4B5563]'}`}
                            onClick={() => {
                              setSortBy(option.value);
                              setIsSortDropdownOpen(false);
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {Object.keys(selectedFilters).length > 0 && (
              <div className="mb-6 flex flex-wrap gap-3">
                {Object.entries(selectedFilters).map(([filterName, values]) =>
                  values.map((value) => (
                    <div
                      key={`${filterName}-${value}`}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full bg-white"
                    >
                      <span className="text-sm font-semibold text-gray-900">
                        {value}
                      </span>
                      <button
                        onClick={() =>
                          handleFilterChange(filterName, value, false)
                        }
                        className="text-gray-500 hover:text-gray-900 font-bold"
                        title="Remove filter"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Products Grid */}
            <div>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loading />
                </div>
              ) : filteredProducts.length > 0 ? (
                <div className="grid md:grid-cols-[repeat(auto-fill,minmax(298px,1fr))] grid-cols-2 gap-6">
                  {filteredProducts.map((item) => (
                    <ProductCard
                      key={item?.id}
                      item={item}
                      tag=""
                      showHeart={true}
                      isThisWishlistPage={false}
                    />
                  ))}
                </div>
              ) : searchQuery ? (
                <div className="flex items-center justify-center py-16">
                  <p className="text-lg text-gray-600 font-medium">
                    No products found for "{searchQuery}"
                  </p>
                </div>
              ) : sortBy === "discount" &&
                products.length > 0 &&
                filteredProducts.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                  <p className="text-lg text-gray-600 font-medium">
                    No products have a discount
                  </p>
                </div>
              ) : filteredProducts.length === 0 && !loading ? (
                <div className="mt-[15%]">
                  <div className="flex items-center justify-center">
                    <p className="md:text-lg text-xs text-gray-600 font-medium">
                      We couldn't find any products matching your selection.
                    </p>
                  </div>
                  <div className="flex items-center justify-center">
                    <p className="md:text-lg text-xs text-gray-600 font-medium">
                      adjusting your filters or explore other collections.
                    </p>
                  </div>
                  <div className="flex items-center justify-center mt-[2%]">
                    <button onClick={() => navigate("/")} className="flex w-[26%] p-3 text-[8px] md:text-[18px] text-[#F74F25]  uppercase font-semibold items-center justify-center border-2 border-[#F74F25] ">
                      View all Products
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filters - Hide when searching */}
      {!searchQuery && showMobileFilters && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="text-gray-500 hover:text-gray-900 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Filters Content */}
            <div className="flex-1 overflow-auto">
              <div className="divide-y">
                {filterMasterData?.getFilterMasterByShop &&
                  filterMasterData.getFilterMasterByShop.length > 0 ? (
                  filterMasterData.getFilterMasterByShop.map(
                    (filter, index) => (
                      <AccordionSection
                        key={index}
                        title={capitalizeFirstLetter(
                          filter.specificationMaster
                        )}
                      >
                        {getUniquValues(filter.specificationValue).map(
                          (value, idx) => (
                            <label
                              key={idx}
                              className="flex items-center gap-3 mt-3 cursor-pointer hover:bg-gray-50 p-2 rounded"
                            >
                              <input
                                type="checkbox"
                                className="w-5 h-5 cursor-pointer"
                                checked={
                                  selectedFilters[
                                    filter.specificationMaster
                                  ]?.includes(value) || false
                                }
                                onChange={(e) =>
                                  handleFilterChange(
                                    filter.specificationMaster,
                                    value,
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm text-gray-700">
                                {value}
                              </span>
                            </label>
                          )
                        )}
                      </AccordionSection>
                    )
                  )
                ) : (
                  <>
                    <AccordionSection title="Product Type">
                      <label className="flex items-center gap-3 mt-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="w-5 h-5 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">Crew Neck</span>
                      </label>
                      <label className="flex items-center gap-3 mt-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="w-5 h-5 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">V Neck</span>
                      </label>
                    </AccordionSection>

                    <AccordionSection title="Color">
                      <div className="flex gap-3 flex-wrap mt-3">
                        <button className="w-8 h-8 rounded-full bg-black border-2 border-gray-300" />
                        <button className="w-8 h-8 rounded-full bg-white border-2 border-gray-300" />
                        <button className="w-8 h-8 rounded-full bg-red-500 border-2 border-gray-300" />
                      </div>
                    </AccordionSection>

                    <AccordionSection title="Price Range">
                      <div className="text-sm text-gray-600 mt-3">
                        ₹0 - ₹999
                      </div>
                    </AccordionSection>

                    <AccordionSection title="Size">
                      <label className="flex items-center gap-3 mt-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="w-5 h-5 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">S</span>
                      </label>
                      <label className="flex items-center gap-3 mt-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          className="w-5 h-5 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">M</span>
                      </label>
                    </AccordionSection>
                  </>
                )}
              </div>
            </div>

            {/* Footer with Apply/Clear buttons */}
            <div className="border-t border-gray-200 px-4 py-4 flex gap-3">
              <button
                onClick={() => {
                  clearAllFilters();
                  setShowMobileFilters(false);
                }}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-900 border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-primary-default rounded"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProducts;